import { useCallback, useEffect, useRef, useState, type RefObject, type SyntheticEvent } from 'react'

/**
 * Media responses are intentionally short lived when shared storage is used.
 * The authenticated /api/media boundary can mint a fresh redirect, so a
 * player may retry that boundary a small number of times after an expired
 * signed response without getting stuck in an interval or a retry loop.
 */
export const MAX_MEDIA_RECOVERY_ATTEMPTS = 2
/** Require a short healthy window before opening another bounded retry budget. */
export const HEALTHY_RECOVERY_WINDOW_MS = 1500

export interface PreservedPlayback {
  time: number
  shouldResume: boolean
}

export function mediaOrigin() {
  return typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'http://motion.local'
}

export function preservePlayback(element: Pick<HTMLMediaElement, 'currentTime' | 'paused' | 'ended'>, intendedPlaying = false): PreservedPlayback {
  return {
    time: Number.isFinite(element.currentTime) ? Math.max(0, element.currentTime) : 0,
    shouldResume: intendedPlaying || (!element.paused && !element.ended),
  }
}

/**
 * Return a fresh authenticated media boundary URL. Direct provider URLs are
 * deliberately not retried because adding a cache key cannot renew their
 * credentials; callers should pass the corresponding /api/media URL as the
 * stable URL when one is available.
 */
export function refreshedMediaUrl(currentUrl: string | null | undefined, stableUrl: string | null | undefined, attempt: number, origin = 'http://motion.local'): string | null {
  if (attempt < 1 || attempt > MAX_MEDIA_RECOVERY_ATTEMPTS) return null
  const base = stableUrl || currentUrl
  if (!base) return null
  let parsed: URL
  try {
    parsed = new URL(base, origin)
  } catch {
    return null
  }
  const expectedOrigin = new URL(origin).origin
  if (parsed.origin !== expectedOrigin || !parsed.pathname.startsWith('/api/')) return null
  parsed.searchParams.set('media_retry', `${Date.now().toString(36)}-${attempt}`)
  // Keep relative URLs relative so API calls remain on the current app host.
  return base.startsWith('/') ? `${parsed.pathname}${parsed.search}` : parsed.toString()
}

export function planMediaRecovery(element: Pick<HTMLMediaElement, 'currentTime' | 'paused' | 'ended'>, currentUrl: string | null | undefined, stableUrl: string | null | undefined, attempt: number, origin = 'http://motion.local', intendedPlaying = false) {
  const url = refreshedMediaUrl(currentUrl, stableUrl, attempt, origin)
  return url ? { url, playback: preservePlayback(element, intendedPlaying) } : null
}

interface RecoveryEvent {
  currentTarget: HTMLVideoElement
}

interface MediaOverride {
  url: string | null
  src: string | null
  key: string
  attempt: number
}

/**
 * Shared video recovery state for Review, Calendar, and saved recordings.
 * Changing the retry key remounts the element, while pending playback restores
 * the old position and play state on the fresh media metadata event.
 */
export function useMediaRecovery(videoRef: RefObject<HTMLVideoElement>, initialUrl: string | null | undefined, stableUrl?: string | null, intendedPlayingRef?: RefObject<boolean>) {
  const url = initialUrl || null
  // src and key follow the URL in the same render. Copying the URL into state
  // from an effect left the element one render behind a newly selected clip,
  // so its play request found no <video>. A retry or manual reset overrides
  // them only while the URL it was made for is still current.
  const [override, setOverride] = useState<MediaOverride | null>(null)
  const current = override?.url === url ? override : null
  const src = current ? current.src : url
  const key = current ? current.key : url || 'empty'
  const attempt = current ? current.attempt : 0
  const pendingRef = useRef<PreservedPlayback | null>(null)
  const healthyTimerRef = useRef<number | null>(null)

  useEffect(() => {
    pendingRef.current = null
    if (healthyTimerRef.current !== null) window.clearTimeout(healthyTimerRef.current)
    setOverride(null)
  }, [url])

  // A successful retry must only reset the consecutive-failure budget after
  // the new element has stayed playable briefly. Binding through videoRef
  // keeps this independent of React's synthetic event ordering and ensures a
  // third later expiry can renew the same stable media boundary.
  useEffect(() => {
    const element = videoRef.current
    if (!element || attempt === 0) return
    let disposed = false
    const clearHealthyTimer = () => {
      if (healthyTimerRef.current !== null) {
        window.clearTimeout(healthyTimerRef.current)
        healthyTimerRef.current = null
      }
    }
    const markHealthy = () => {
      clearHealthyTimer()
      healthyTimerRef.current = window.setTimeout(() => {
        healthyTimerRef.current = null
        if (!disposed) setOverride((previous) => previous && { ...previous, attempt: 0 })
      }, HEALTHY_RECOVERY_WINDOW_MS)
    }
    const markUnhealthy = () => clearHealthyTimer()
    element.addEventListener('canplay', markHealthy)
    element.addEventListener('playing', markHealthy)
    element.addEventListener('error', markUnhealthy)
    if (element.readyState >= 3) markHealthy()
    return () => {
      disposed = true
      clearHealthyTimer()
      element.removeEventListener('canplay', markHealthy)
      element.removeEventListener('playing', markHealthy)
      element.removeEventListener('error', markUnhealthy)
    }
  }, [attempt, key, videoRef])

  const onError = useCallback((event: SyntheticEvent<HTMLVideoElement> | RecoveryEvent) => {
    const nextAttempt = attempt + 1
    const plan = planMediaRecovery(event.currentTarget, src || url, stableUrl, nextAttempt, mediaOrigin(), intendedPlayingRef?.current || false)
    if (!plan) return false
    pendingRef.current = plan.playback
    setOverride({ url, src: plan.url, key: `${url || 'media'}|retry-${nextAttempt}`, attempt: nextAttempt })
    return true
  }, [attempt, url, src, stableUrl])

  const onLoadedMetadata = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    const pending = pendingRef.current
    if (!pending) return
    pendingRef.current = null
    const element = event.currentTarget
    if (pending.time > 0 && Number.isFinite(element.duration) && element.duration > 0) {
      try { element.currentTime = Math.min(pending.time, Math.max(0, element.duration - 0.05)) } catch { /* Browser may defer seeking until the first frame. */ }
    }
    if (pending.shouldResume) void element.play().catch(() => undefined)
  }, [])

  const reset = useCallback(() => {
    pendingRef.current = null
    if (healthyTimerRef.current !== null) {
      window.clearTimeout(healthyTimerRef.current)
      healthyTimerRef.current = null
    }
    setOverride({ url, src: url, key: `${url || 'empty'}|manual-${Date.now().toString(36)}`, attempt: 0 })
  }, [url])

  return { src, key, attempt, onError, onLoadedMetadata, reset }
}
